import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { BookOpen, Filter, ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import type { GlossaryTerm } from "@/content/glossary/terms";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

interface GlossaryCard {
  slug: string;
  term: string;
  termEn: string;
  shortDef: string;
  alsoKnownAs?: string[];
  category: GlossaryTerm["category"];
  categoryLabel: string;
}

interface GlossaryHubData {
  terms: GlossaryCard[];
  categories: { key: GlossaryTerm["category"]; label: string; count: number }[];
}

export const Route = createFileRoute("/glossary/")({
  component: GlossaryHub,
  loader: async (): Promise<GlossaryHubData> => {
    const { GLOSSARY, GLOSSARY_CATEGORIES } = await import("@/content/glossary/terms");
    return {
      terms: GLOSSARY.map((t) => ({
        slug: t.slug,
        term: t.term,
        termEn: t.termEn,
        shortDef: t.shortDef,
        alsoKnownAs: t.alsoKnownAs,
        category: t.category,
        categoryLabel: GLOSSARY_CATEGORIES[t.category],
      })),
      categories: (Object.keys(GLOSSARY_CATEGORIES) as GlossaryTerm["category"][]).map((k) => ({
        key: k,
        label: GLOSSARY_CATEGORIES[k],
        count: GLOSSARY.filter((t) => t.category === k).length,
      })),
    };
  },
  head: ({ loaderData }) => {
    const terms = (loaderData as GlossaryHubData | undefined)?.terms ?? [];
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `อภิธานศัพท์ Notary / Visa / กงสุล ${terms.length}+ คำ — Thai Notary`,
      description: `คำอธิบายศัพท์ทางกฎหมายไทย-อังกฤษ ${terms.length}+ คำ — Notary, MFA, Apostille, Affidavit, POA, NAATI, MoJ, DTV, LTR, Elite, Work Permit พร้อมตัวอย่างใช้งานจริง`,
      path: "/glossary",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/glossary")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "อภิธานศัพท์", url: "/glossary" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTermSet",
            name: "Thai Notary Legal Glossary",
            url: `${site}/glossary`,
            hasDefinedTerm: terms.map((t) => ({
              "@type": "DefinedTerm",
              "@id": `${site}/glossary/${t.slug}#term`,
              name: t.term,
              alternateName: t.alsoKnownAs ?? [t.termEn],
              description: t.shortDef,
              url: `${site}/glossary/${t.slug}`,
            })),
          }),
        },
      ],
    };
  },
});

type CategoryKey = GlossaryTerm["category"] | "all";

function GlossaryHub() {
  const { terms: GLOSSARY, categories } = Route.useLoaderData() as GlossaryHubData;
  const [cat, setCat] = useState<CategoryKey>("all");
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return GLOSSARY.filter((t) => {
      const matchCat = cat === "all" || t.category === cat;
      const matchQ =
        !norm ||
        t.term.toLowerCase().includes(norm) ||
        t.termEn.toLowerCase().includes(norm) ||
        (t.alsoKnownAs ?? []).some((a) => a.toLowerCase().includes(norm)) ||
        t.shortDef.toLowerCase().includes(norm);
      return matchCat && matchQ;
    });
  }, [cat, q]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold">
              <BookOpen className="h-3.5 w-3.5" /> Glossary · {GLOSSARY.length} terms
            </div>
            <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">
              อภิธานศัพท์ Notary / Visa / กงสุล
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              รวมคำศัพท์กฎหมายและเอกสารที่เจอบ่อยที่สุดในงาน Notary, Visa, MFA, Embassy
              และจดทะเบียนสมรส — แต่ละคำมีนิยามสั้น (สำหรับ AI Quick Answer), อธิบายยาว,
              FAQ, และลิงก์ไปบริการ/บทความที่เกี่ยวข้อง
            </p>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="ค้นหา เช่น Apostille, NAATI, DTV…"
              className="mt-6 w-full max-w-xl rounded-lg border border-border bg-card px-4 py-3 text-sm placeholder:text-muted-foreground focus:border-gold focus:outline-none"
            />
          </div>
        </section>

        <section className="sticky top-0 z-20 border-b border-border bg-background/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-2 px-4 py-3">
            <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <Filter className="h-3.5 w-3.5" /> หมวด
            </span>
            {([{ key: "all" as const, label: `ทั้งหมด (${GLOSSARY.length})`, count: GLOSSARY.length }, ...categories]).map((entry) => {
              const c = entry.key as CategoryKey;
              const label = entry.label;
              const count = entry.count;
              if (c !== "all" && count === 0) return null;
              return (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                    cat === c
                      ? "border-gold bg-gold text-foreground"
                      : "border-border bg-card text-muted-foreground hover:border-gold"
                  }`}
                >
                  {c === "all" ? label : `${label} (${count})`}
                </button>
              );
            })}
          </div>
        </section>

        <section className="border-b border-border">
          <div className="mx-auto grid max-w-6xl gap-4 px-4 py-10 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((t) => (
              <Link
                key={t.slug}
                to="/glossary/$term"
                params={{ term: t.slug }}
                className="group flex flex-col rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-gold hover:shadow-lg"
              >
                <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                  {t.categoryLabel}
                </div>
                <h2 className="mt-1 text-base font-extrabold text-foreground group-hover:text-primary">
                  {t.term}
                </h2>
                <div className="text-xs text-muted-foreground">{t.termEn}</div>
                <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{t.shortDef}</p>
                <div className="mt-auto pt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                  อ่านเพิ่ม <ArrowRight className="h-3 w-3 transition group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
                ไม่พบคำที่ค้นหา — ลองคำอื่น เช่น “Notary”, “Apostille”, “DTV”
              </div>
            )}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}